import type { User } from "../slices/authSlice"
import CurrencySelector from "../components/layout/CurrencySelector"
import { useEffect, useState } from "react"
import { SpinnerText } from "../components/ui/Spinner"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useSelector } from "react-redux"
import AppLayout from "../layout/AppLayout"
import PatentFormStepper, {
  PatentFormStep
} from "../components/patent/PatentFormStepper"
import TitleSection, { TitleTranslations } from "../components/patent/TitleSection"
import ReferenceNumberSection from "../components/trademark/reference_number/ReferenceNumberSection"
import PatentApplicantAndRepresentative from "../components/patent/PatentApplicantAndRepresentative"
import InventorSection from "../components/patent/InventorSection"
import PrioritySection from "../components/patent/PrioritySection"
import DesignationStatesSection, {
  DesignationStatesValue
} from "../components/patent/DesignationStatesSection"
import DocumentsSection from "../components/patent/DocumentsSection"
import PatentFeesSection from "../components/patent/PatentFeesSection"
import PatentApplicationFeesBox from "../components/patent/PatentApplicationFeesBox"
import ServiceDescriptions from "../components/ui/ServiceDescriptions"
import { FilingSidebarAccordion } from "../components/ui/FilingSidebarAccordion"
import PatentLanguageSection from "../components/patent/PatentLanguageSection"
import PatentDeclarationsSection, {
  PatentDeclarationsValue,
  EMPTY_PATENT_DECLARATIONS
} from "../components/patent/PatentDeclarationsSection"
import SignatureSection from "../components/trademark/signature/SignatureSection"
import { resetForm } from "../hooks/resetForm"
import { useDraftStep } from "../hooks/useDraftStep"
import StatusToast from "../components/ui/StatusToast"
import StatusBadge from "../components/ui/StatusBadge"
import {
  getPatentApplicationById,
  updatePatentApplicationStatus,
  validatePatentApplication
} from "../services/patentApplicationService"
import { savePatentDraft } from "../services/savePatentDraftService"
import { formatApiError } from "../utils/formatApiError"
import { RootState } from "../store/store"
import { FiCreditCard, FiRefreshCw, FiSave, FiX } from "react-icons/fi"
import { PATENT_PROCEDURE_LABELS } from "../utils/patentProcedure"

const REQUIRED_DOCUMENT_TYPES = ["DESCRIPTION", "CLAIMS", "ABSTRACT"]

function requiredDocumentTypes(claimsFiledLater: boolean) {
  return claimsFiledLater
    ? REQUIRED_DOCUMENT_TYPES.filter((type) => type !== "CLAIMS")
    : REQUIRED_DOCUMENT_TYPES
}

const EMPTY_DESIGNATION_STATES: DesignationStatesValue = {
  differentContractingStatesPerApplicant: false,
  extensionStatesEnabled: false,
  extensionStateMode: "all",
  selectedExtensionStates: [],
  validationStatesEnabled: false,
  validationStateMode: "all",
  selectedValidationStates: []
}

// "Anmelder ist auch Erfinder" (SCRUM-55): a natural-person applicant
// who ticked that box must also appear in the inventors list. This is a
// one-way convenience — it adds a matching inventor when missing (keyed
// by name), it never edits or removes one the user manages by hand.
function inventorKey(inv: any) {
  return `${inv.firstName || ""} ${inv.lastName || ""}`
    .trim()
    .toLowerCase() || (inv.name || "").trim().toLowerCase()
}

function mergeApplicantInventors(applicants: any[] | null, inventors: any[]) {
  const fromApplicants = (applicants || [])
    .filter((a) => a && !a.isDeleted && a.type === "natural" && a.isAlsoInventor)
    .map((a) => ({
      firstName: a.firstName || "",
      lastName: a.lastName || "",
      deceased: false,
      residence: {
        postalCode: a.address?.postalCode || undefined,
        city: a.address?.city || "",
        country: a.address?.country || ""
      },
      waiverIndicator: false,
      applicantIsInventor: true
    }))
    .filter((inv) => inv.firstName || inv.lastName)

  const existingKeys = new Set((inventors || []).map(inventorKey))

  const additions = fromApplicants.filter(
    (inv) => !existingKeys.has(inventorKey(inv))
  )

  return additions.length > 0 ? [...(inventors || []), ...additions] : inventors
}

// Drafts saved before SCRUM-55 split the inventor fields: bring their
// single `name`/`address` string and the old application-level
// origin-of-right onto the current per-inventor shape.
function migrateLegacyInventors(app: any) {
  const legacyOrigin: Record<string, string> = {
    EMPLOYMENT: "EMPLOYER",
    LEGAL_SUCCESSOR: "LEGAL_SUCCESSOR",
    AGREEMENT: "AGREEMENT"
  }
  const oldOrigin = app.inventorDesignation?.originOfRight
  const applicantIsInventor = oldOrigin === "APPLICANT_IS_INVENTOR"
  const rightAcquisition = legacyOrigin[oldOrigin] || undefined

  return (app.inventors || []).map((inv: any) => {
    if (inv.firstName || inv.lastName || inv.residence) return inv

    const parts = (inv.name || "").trim().split(/\s+/)
    return {
      firstName: parts.slice(0, -1).join(" "),
      lastName: parts.length > 1 ? parts.slice(-1)[0] : parts[0] || "",
      deceased: false,
      residence: { postalCode: "", city: "", country: "" },
      address: inv.address,
      waiverIndicator: Boolean(inv.waiverIndicator),
      applicantIsInventor,
      rightAcquisition: applicantIsInventor ? undefined : rightAcquisition
    }
  })
}

function normalizeDesignationStates(value: any): DesignationStatesValue {
  return {
    ...EMPTY_DESIGNATION_STATES,
    ...(value || {}),
    selectedExtensionStates: Array.isArray(value?.selectedExtensionStates)
      ? value.selectedExtensionStates
      : [],
    selectedValidationStates: Array.isArray(value?.selectedValidationStates)
      ? value.selectedValidationStates
      : []
  }
}

// Form skeleton only (SCRUM-42): reference number, draft save/reset,
// status, loading state. The actual form sections (Anmelder/Erfinder,
// Titel, Priorität, Unterlagen, Sprache, Vertreter, Staaten, Gebühren,
// Erfinderbenennung, Unterschrift) are added incrementally by their own
// tickets (SCRUM-43 through SCRUM-53) into the placeholder area below.
// Navigation/routing to reach this page is wired up separately (SCRUM-46).
// Titel der Erfindung (title of the invention) was added by SCRUM-44.
// Priorität (priority claim) was added by SCRUM-45.
// Benennung von Vertragsstaaten, Erstreckungsstaaten und
// Validierungsstaaten was added by SCRUM-50.
// Unterlagen (filing documents) was added by SCRUM-47.
// Sprache (language of filing) was added by SCRUM-48.
// Vertreter (representative) was wired up by SCRUM-49: the platform's own
// representative (fixed, not user-editable — same as Trademark/Design)
// is now actually persisted, and the reused display card is relabelled
// EPO instead of EUIPO for a patent filing.
// Erfinderbenennung (designation of inventor, Rule 19(1)(d) EPC — the
// origin-of-right statement beyond the plain inventor list from
// SCRUM-43) was added by SCRUM-52.
// Unterschrift und Einreichung (signature and submission) was added by
// SCRUM-53, which also finally wires up the payment page.
// SCRUM-55 (UI reconciled against the real EPO tool) moved Sprache
// before Titel to match the real form's order, split it into the
// language of the proceedings and the (usually identical) language of
// the application, added optional title translations, an Art. 14(4) EPC
// non-official-examination-language request next to Sprache, a static
// "Filing office" label, and the remaining "Allgemeine Angaben"
// declarations (Teilanmeldung, Art. 61(1)(b), Regel 40(1)(c),
// Regel 70(2), Regel 7a fee reductions) as their own section.
// SCRUM-55 also replaced the shared applicant wrapper with the
// patent-specific PatentApplicantAndRepresentative: a dedicated EP1001
// applicant form (all EPO fields, natural + legal) plus the fixed EPO
// representative. A natural-person applicant who ticks "Applicant is
// also inventor" is added to the inventors list on save
// (mergeApplicantInventors). SCRUM-55 further aligned the inventor
// section with the real "Erfinder" form and dropped the standalone
// InventorDesignationSection: the Rule 19(1)(d) origin-of-right
// statement now lives on each inventor (rightAcquisition), migrated
// from the old application-level field for existing drafts
// (migrateLegacyInventors). The Documents section gained the "claims /
// translation filed later" declarations — the former drops the claims
// upload from the required set.
// SCRUM-55 also split the single long scroll into a step flow
// (PatentFormStepper) mirroring the real EPO tool's sidebar:
// 1. General information · 2. Parties & inventors · 3. Documents ·
// 4. Fees · 5. Signature. Steps are freely navigable; every section stays
// mounted (hidden) so nothing is lost when switching, and the draft is
// saved silently on each step change.

const FORM_STEPS = [
  "General information",
  "Parties & inventors",
  "Documents",
  "Fees",
  "Signature"
]

type PatentProcedureType = "EP1001" | "EP1200"

function normalizeProcedureType(value: string | null | undefined): PatentProcedureType {
  return value === "EP1200" ? "EP1200" : "EP1001"
}

export default function PatentFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const isViewMode = location.pathname.includes("/view")
  const isAdmin = useSelector((state: RootState) => Boolean(state.auth.user?.isAdmin))
  const [procedureType, setProcedureType] = useState<PatentProcedureType>(
    normalizeProcedureType(new URLSearchParams(location.search).get("procedureType"))
  )
  const [referenceNumber, setReferenceNumber] = useState("")
  const [title, setTitle] = useState("")
  const [titleTranslations, setTitleTranslations] = useState<TitleTranslations>({})
  const [applicants, setApplicants] = useState<any[] | null>(null)
  const [inventors, setInventors] = useState<any[]>([])
  const [priorities, setPriorities] = useState<any[] | null>(null)
  const [designationStates, setDesignationStates] =
    useState<DesignationStatesValue>(EMPTY_DESIGNATION_STATES)
  const [documents, setDocuments] = useState<any[] | null>(null)
  const [claimsFiledLater, setClaimsFiledLater] = useState(false)
  const [translationFiledLater, setTranslationFiledLater] = useState(false)
  const [numberOfClaims, setNumberOfClaims] = useState<number | null>(null)
  const [filingLanguage, setFilingLanguage] = useState("")
  const [applicationLanguage, setApplicationLanguage] = useState("")
  const [examinationInNonOfficialLanguage, setExaminationInNonOfficialLanguage] =
    useState(false)
  const [declarations, setDeclarations] = useState<PatentDeclarationsValue>(
    EMPTY_PATENT_DECLARATIONS
  )
  const [signature, setSignature] = useState<any | null>(null)
  const [orderingParty, setOrderingParty] = useState<Partial<User> | null | undefined>(undefined)
  const [draftId, setDraftId] = useState<string | null>(null)
  const [feeRefreshToken, setFeeRefreshToken] = useState(0)
  const [signatureId, setSignatureId] = useState<string | null>(null)
  const [applicationStatus, setApplicationStatus] = useState<"DRAFT" | "VALIDATED" | "AWAITING_RELEASE" | "PAYMENT_PENDING" | "ERROR" | "PAID">("DRAFT")
  const [validationError, setValidationError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [loading, setLoading] = useState(Boolean(id))
  const [showResetProcedureDialog, setShowResetProcedureDialog] = useState(false)
  const [resetProcedureType, setResetProcedureType] = useState<PatentProcedureType>("EP1001")
  const isSubmitted =
    applicationStatus === "PAID" ||
    applicationStatus === "PAYMENT_PENDING" ||
    applicationStatus === "AWAITING_RELEASE"
  const isPaid = isViewMode || (!isAdmin && isSubmitted)
  const { step, setStep, savingDraft, goToStep: goToStepBase, runSaving } = useDraftStep(isPaid)
  const statusMustStayLocked =
    applicationStatus === "PAID" ||
    applicationStatus === "PAYMENT_PENDING" ||
    applicationStatus === "AWAITING_RELEASE" ||
    applicationStatus === "ERROR"

  // Merge "applicant is also inventor" into the inventors list as soon as
  // the applicant is saved, instead of only at the next full draft save
  // (savePatentApplication already does this too, as a safety net for
  // applicants set outside this handler, e.g. on load).
  const handleApplicantChange = (nextApplicants: any[]) => {
    setApplicants(nextApplicants)
    setInventors((prevInventors) => mergeApplicantInventors(nextApplicants, prevInventors))
  }

  const savePatentApplication = async () => {
    if (isPaid) {
      throw new Error("Paid applications cannot be edited.")
    }

    const effectiveInventors = mergeApplicantInventors(applicants, inventors)
    if (effectiveInventors !== inventors) {
      setInventors(effectiveInventors)
    }

    const nextDraftId = await savePatentDraft({
      procedureType,
      referenceNumber,
      title,
      titleTranslations,
      applicants,
      inventors: effectiveInventors,
      priorities,
      designationStates,
      documents,
      filingLanguage,
      applicationLanguage,
      examinationInNonOfficialLanguage,
      declarations,
      claimsFiledLater,
      translationFiledLater,
      numberOfClaims,
      signature,
      draftId,
      signatureId,
      onDraftSaved: setDraftId,
      onSignatureSaved: setSignatureId
    })

    setDraftId(nextDraftId)
    setFeeRefreshToken((token) => token + 1)

    if (nextDraftId && !statusMustStayLocked) {
      await updatePatentApplicationStatus(nextDraftId, "DRAFT").catch(() => {})
    }

    if (!statusMustStayLocked) {
      setApplicationStatus("DRAFT")
    }

    return nextDraftId
  }

  const closeResetProcedureDialog = () => {
    setResetProcedureType("EP1001")
    setShowResetProcedureDialog(false)
  }

  const handleOpenResetProcedureDialog = () => {
    setResetProcedureType("EP1001")
    setShowResetProcedureDialog(true)
  }

  const handleResetPatentApplication = () => runSaving(async () => {
    try {
      const deletedApplicants = (applicants || []).map((applicant) => ({
        ...applicant,
        isDeleted: true
      }))
      const deletedPriorities = (priorities || []).map((priority) => ({
        ...priority,
        isDeleted: true
      }))

      const nextDraftId = await savePatentDraft({
        procedureType: resetProcedureType,
        referenceNumber: "",
        title: "",
        titleTranslations: {},
        applicants: deletedApplicants,
        inventors: [],
        priorities: deletedPriorities,
        designationStates: EMPTY_DESIGNATION_STATES,
        documents: [],
        filingLanguage: "",
        applicationLanguage: "",
        examinationInNonOfficialLanguage: false,
        declarations: EMPTY_PATENT_DECLARATIONS,
        claimsFiledLater: false,
        translationFiledLater: false,
        numberOfClaims: null,
        signature: null,
        draftId,
        signatureId,
        onDraftSaved: setDraftId,
        onSignatureSaved: setSignatureId
      })

      resetForm()
      setDraftId(nextDraftId)
      setProcedureType(resetProcedureType)
      setReferenceNumber("")
      setTitle("")
      setTitleTranslations({})
      setApplicants([])
      setInventors([])
      setPriorities([])
      setDesignationStates(EMPTY_DESIGNATION_STATES)
      setDocuments([])
      setClaimsFiledLater(false)
      setTranslationFiledLater(false)
      setFilingLanguage("")
      setApplicationLanguage("")
      setExaminationInNonOfficialLanguage(false)
      setDeclarations(EMPTY_PATENT_DECLARATIONS)
      setSignature(null)
      setStep(0)
      setValidationError("")
      setSuccessMessage("Patent application draft reset successfully.")

      if (nextDraftId && !statusMustStayLocked) {
        await updatePatentApplicationStatus(nextDraftId, "DRAFT").catch(() => {})
        setApplicationStatus("DRAFT")
      }

      setShowResetProcedureDialog(false)

      if (resetProcedureType === "EP1200") {
        navigate(`/patent-applications/${nextDraftId}/ep1200/edit`)
      }
    } catch (error) {
      console.error("Patent application could not be reset:", error)
      setSuccessMessage("")
      setValidationError(
        formatApiError(
          error,
          "The patent application could not be reset. Please reload the page and try again."
        )
      )
    }
  })

  const handleSavePatentApplication = () => runSaving(async () => {
    try {
      await savePatentApplication()
      setValidationError("")
      setSuccessMessage("Patent application draft saved successfully.")
    } catch (error) {
      console.error("Patent application could not be saved:", error)
      setSuccessMessage("")
      setValidationError(
        formatApiError(
          error,
          "The patent application could not be saved. Please reload the page and try again."
        )
      )
    }
  })

  const uploadedDocumentTypes = new Set((documents || []).map((doc) => doc.type))
  const requiredDocs = requiredDocumentTypes(claimsFiledLater)
  // EPO needs the number of claims whenever the claims are filed now.
  const claimsCountOk = claimsFiledLater || (numberOfClaims !== null && numberOfClaims > 0)
  const paymentEnabled =
    !isSubmitted &&
    !isPaid &&
    !!title.trim() &&
    !!applicants?.length &&
    !!inventors.length &&
    inventors.every(
      (inv) => inv.applicantIsInventor || inv.rightAcquisition
    ) &&
    requiredDocs.every((type) => uploadedDocumentTypes.has(type)) &&
    claimsCountOk &&
    !!filingLanguage &&
    !!signature?.accepted

  const stepComplete = [
    !!filingLanguage && !!title.trim(),
    !!applicants?.length &&
      inventors.length > 0 &&
      inventors.every((inv) => inv.applicantIsInventor || inv.rightAcquisition),
    requiredDocs.every((type) => uploadedDocumentTypes.has(type)) && claimsCountOk,
    true, // Fees: every item is optional, so this step is never "incomplete"
    !!signature?.accepted
  ]

  const steps: PatentFormStep[] = FORM_STEPS.map((label, index) => ({
    key: label,
    label,
    complete: stepComplete[index]
  }))

  const goToStep = (index: number) =>
    goToStepBase(index, savePatentApplication, Boolean(draftId || referenceNumber || title))

  const handleProceedToPayment = () => {
    if (!paymentEnabled) return
    return runSaving(async () => {
      try {
        const nextDraftId = await savePatentApplication()

        if (!nextDraftId) {
          throw new Error("Draft could not be saved.")
        }

        await validatePatentApplication(nextDraftId)
        await updatePatentApplicationStatus(nextDraftId, "VALIDATED")
        setApplicationStatus("VALIDATED")
        setValidationError("")
        setSuccessMessage("")
        navigate(`/patent-applications/${nextDraftId}/payment`)
      } catch (error) {
        console.error("Patent application could not be validated:", error)
        setValidationError(
          formatApiError(
            error,
            "The patent application could not be validated. Please review the highlighted fields and try again."
          )
        )
      }
    })
  }

  useEffect(() => {
    const patentApplicationId = id

    if (!patentApplicationId) {
      if (procedureType === "EP1200") {
        navigate("/patent-applications/ep1200/new", { replace: true })
        return
      }
      setLoading(false)
      return
    }

    async function load() {
      try {
        const app = await getPatentApplicationById(patentApplicationId as string)

        if (app.procedureType === "EP1200") {
          navigate(
            `/patent-applications/${patentApplicationId}/${isViewMode ? "ep1200/view" : "ep1200/edit"}`,
            { replace: true }
          )
          return
        }

        setOrderingParty(typeof app.userId === "object" ? app.userId : null)
        setDraftId(app._id)
        setProcedureType(normalizeProcedureType(app.procedureType))
        setApplicationStatus(app.status || "DRAFT")
        setReferenceNumber(app.referenceNumber?.referenceNumber || "")
        setTitle(app.title || "")
        setTitleTranslations(app.titleTranslations || {})
        setApplicants(app.applicants || [])
        setInventors(migrateLegacyInventors(app))
        setPriorities(app.priorities || [])
        setDesignationStates(normalizeDesignationStates(app.designationStates))
        setDocuments(app.documents || [])
        setClaimsFiledLater(Boolean(app.claimsFiledLater))
        setTranslationFiledLater(Boolean(app.translationFiledLater))
        setNumberOfClaims(app.numberOfClaims ?? null)
        setFilingLanguage(app.languages?.filingLanguage || "")
        setApplicationLanguage(app.applicationLanguage || app.languages?.filingLanguage || "")
        setExaminationInNonOfficialLanguage(
          Boolean(app.examinationInNonOfficialLanguage)
        )
        setDeclarations({
          ...EMPTY_PATENT_DECLARATIONS,
          ...(app.declarations || {})
        })

        if (app.signature) {
          setSignatureId(app.signature._id)
          setSignature({ ...app.signature, accepted: true })
        } else {
          setSignatureId(null)
          setSignature(null)
        }
      } catch (error) {
        setValidationError(
          formatApiError(
            error,
            "The patent application could not be loaded. Please reload the page and try again."
          )
        )
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [id, isViewMode, navigate, procedureType])

  if (loading) {
    return (
      <AppLayout title={PATENT_PROCEDURE_LABELS.EP1001} wide>
        <div className="flex justify-center py-32 text-gray-600">
          <SpinnerText>Loading application...</SpinnerText>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout title={PATENT_PROCEDURE_LABELS.EP1001} wide>
      {validationError && (
        <StatusToast
          type="error"
          message={validationError}
          onClose={() => setValidationError("")}
        />
      )}

      {successMessage && (
        <StatusToast
          type="success"
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}

      {savingDraft && <StatusToast type="loading" message="Saving your patent application…" />}

      {showResetProcedureDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
          <div className="w-full max-w-lg rounded-xl border border-secondary5 bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-heading text-lg font-medium text-dantzBlue">
                  Reset patent application
                </h2>
                <p className="mt-1 text-sm text-secondary4">
                  Select the filing type for the cleared draft.
                </p>
              </div>
              <button
                type="button"
                onClick={closeResetProcedureDialog}
                disabled={savingDraft}
                className="rounded-md p-1 text-secondary4 transition hover:bg-dantzGray hover:text-dantzBlue disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dantzOrange"
                aria-label="Close"
              >
                <FiX size={20} />
              </button>
            </div>

            <label className="mt-5 block text-sm font-medium text-secondary3">
              Filing type
            </label>
            <select
              value={resetProcedureType}
              onChange={(event) =>
                setResetProcedureType(event.target.value as PatentProcedureType)
              }
              disabled={savingDraft}
              className="mt-2 w-full rounded-lg border border-secondary5 bg-white px-3 py-2.5 text-sm text-dantzBlue shadow-sm focus:border-dantzOrange focus:outline-none focus:ring-1 focus:ring-dantzOrange disabled:cursor-not-allowed disabled:bg-gray-100"
            >
              <option value="EP1001">
                {PATENT_PROCEDURE_LABELS.EP1001}
              </option>
              <option value="EP1200">
                {PATENT_PROCEDURE_LABELS.EP1200}
              </option>
            </select>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeResetProcedureDialog}
                disabled={savingDraft}
                className="rounded-lg border border-secondary5 bg-white px-4 py-2 text-sm font-medium text-secondary3 transition hover:bg-dantzGray disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleResetPatentApplication}
                disabled={savingDraft}
                className="rounded-lg bg-dantzBlue px-4 py-2 text-sm font-medium text-white transition hover:bg-secondary1 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dantzOrange"
              >
                {savingDraft ? <SpinnerText>Resetting...</SpinnerText> : "Reset and save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="sticky top-0 z-30 border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <PatentFormStepper
            steps={steps}
            current={step}
            onSelect={goToStep}
            disabled={savingDraft}
          />
        </div>
      </div>

      <div className="flex justify-center py-12 pb-28">
        <div className="grid grid-cols-1 gap-7 max-w-7xl w-full xl:grid-cols-[minmax(0,1fr)_18rem]">
          <div className="p-5 sm:p-8 xl:p-12 bg-white border rounded-2xl shadow-sm">
            <div className={`flex-col gap-10 ${step === 0 ? "flex" : "hidden"}`}>
              <div>
                <ReferenceNumberSection
                  value={referenceNumber}
                  onChange={setReferenceNumber}
                  disabled={isPaid}
                />
                <p className="mt-2 text-sm text-gray-500">
                  Filing office: European Patent Office
                </p>
              </div>

              <PatentLanguageSection
                proceedingsLanguage={filingLanguage}
                onProceedingsLanguageChange={setFilingLanguage}
                applicationLanguage={applicationLanguage}
                onApplicationLanguageChange={setApplicationLanguage}
                examinationInNonOfficialLanguage={examinationInNonOfficialLanguage}
                onExaminationInNonOfficialLanguageChange={setExaminationInNonOfficialLanguage}
                disabled={isPaid}
              />

              <TitleSection
                value={title}
                onChange={setTitle}
                proceedingsLanguage={filingLanguage}
                translations={titleTranslations}
                onTranslationsChange={setTitleTranslations}
                disabled={isPaid}
              />

              <PatentDeclarationsSection
                value={declarations}
                onChange={setDeclarations}
                disabled={isPaid}
              />
            </div>

            <div className={`flex-col gap-10 ${step === 1 ? "flex" : "hidden"}`}>
              <PatentApplicantAndRepresentative
                orderingParty={orderingParty}
                onApplicantChange={handleApplicantChange}
                initialData={applicants ?? []}
                disabled={isPaid}
              />

              <InventorSection
                onChange={setInventors}
                initialData={inventors}
                disabled={isPaid}
              />
            </div>

            <div className={`flex-col gap-10 ${step === 2 ? "flex" : "hidden"}`}>
              <PrioritySection
                onChange={setPriorities}
                initialData={priorities ?? []}
                disabled={isPaid}
              />

              <DesignationStatesSection
                value={designationStates}
                onChange={setDesignationStates}
                disabled={isPaid}
              />

              <DocumentsSection
                onChange={setDocuments}
                initialData={documents ?? []}
                claimsFiledLater={claimsFiledLater}
                onClaimsFiledLaterChange={setClaimsFiledLater}
                translationFiledLater={translationFiledLater}
                onTranslationFiledLaterChange={setTranslationFiledLater}
                numberOfClaims={numberOfClaims}
                onNumberOfClaimsChange={setNumberOfClaims}
                disabled={isPaid}
              />
            </div>

            <div className={`flex-col gap-10 ${step === 3 ? "flex" : "hidden"}`}>
              <PatentFeesSection applicationId={draftId} disabled={isPaid} />
            </div>

            <div className={`flex-col gap-10 ${step === 4 ? "flex" : "hidden"}`}>
              <div id="signature">
                <SignatureSection
                  onChange={setSignature}
                  initialData={signature}
                  hideCapacity
                  hideNotice
                  disabled={isPaid}
                />
              </div>
            </div>

            <div className="mt-10 flex items-center justify-between border-t border-gray-100 pt-6">
              <button
                type="button"
                hidden={step === 0}
                onClick={() => goToStep(step - 1)}
                disabled={savingDraft}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                hidden={step === FORM_STEPS.length - 1}
                onClick={() => goToStep(step + 1)}
                disabled={savingDraft}
                className="ml-auto rounded-lg bg-dantzBlue px-5 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>

          <aside className="block">
            <div className="flex w-full flex-col gap-4 xl:sticky xl:top-[128px] xl:max-h-[calc(100vh-9rem)] xl:w-72 xl:overflow-y-auto xl:[scrollbar-width:none] xl:[&::-webkit-scrollbar]:hidden">
              <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-white p-3 text-sm shadow-sm">
                <div className="mb-1 flex items-center justify-between gap-3 border-b border-gray-100 pb-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Status
                  </span>
                  <StatusBadge status={applicationStatus} />
                </div>
                {!isPaid && (
                  <>
                    <button
                      type="button"
                      onClick={handleSavePatentApplication}
                      disabled={savingDraft}
                      className={`flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                        savingDraft
                          ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
                          : "border-gray-300 bg-white text-dantzBlue hover:border-dantzBlue hover:bg-blue-50"
                      }`}
                    >
                      <FiSave className="h-4 w-4" aria-hidden="true" />
                      {savingDraft ? <SpinnerText>Saving draft...</SpinnerText> : "Save as draft"}
                    </button>

                    <button
                      type="button"
                      onClick={handleOpenResetProcedureDialog}
                      disabled={savingDraft}
                      className={`flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                        savingDraft
                          ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
                          : "border-gray-300 bg-white text-dantzBlue hover:border-dantzBlue hover:bg-blue-50"
                      }`}
                    >
                      <FiRefreshCw className="h-4 w-4" aria-hidden="true" />
                      Reset form
                    </button>

                    <button
                      type="button"
                      onClick={handleProceedToPayment}
                      disabled={!paymentEnabled || savingDraft}
                      className={`flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        paymentEnabled && !savingDraft
                          ? "bg-dantzOrange text-white hover:brightness-110 active:scale-[0.98]"
                          : "cursor-not-allowed bg-gray-300 text-gray-500"
                      }`}
                    >
                      <FiCreditCard className="h-4 w-4" aria-hidden="true" />
                      Proceed to payment
                    </button>
                  </>
                )}

                {isPaid && (
                  <p className="mt-2 text-xs text-gray-500">
                    This application has already been submitted and can no longer be edited.
                  </p>
                )}
              </div>
              <FilingSidebarAccordion>
                <PatentApplicationFeesBox
                  applicationId={draftId}
                  procedureType={procedureType === "EP1200" ? "EP1200" : "EP1001"}
                  refreshToken={feeRefreshToken}
                />
                <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
                  <CurrencySelector />
                </div>
                <ServiceDescriptions
                  type={procedureType === "EP1200" ? "patent-pct" : "patent-direct"}
                />
              </FilingSidebarAccordion>
            </div>
          </aside>
        </div>
      </div>
    </AppLayout>
  )
}
